<template>
  <v-dialog
    v-model="dialog"
    max-width="900"
    scrollable
    :retain-focus="false"
  >
    <v-form @submit.prevent="submit">
      <v-card>
        <v-toolbar
          flat
          dark
          color="primary"
        >
          <v-btn
            icon
            @click="goBack"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ question.default_translation.title }}
          <v-spacer />
          <v-btn
            icon
            dark
            @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-0">
          <v-breadcrumbs :items="breadcrumbs" />

          <open-ai-expansion-panel
            class="mb-6"
          >
            <p
              v-if="! faq.openai_description"
              class="mt-2 mb-0"
            >
              {{ $t('You must specify an OpenAI description under "Settings > General" to use this feature') }}
            </p>

            <div v-else>
              <!-- eslint-disable vue/no-v-html -->
              <p
                class="mb-4"
                v-html="$t(`Click the button below to translate the question and answer to {language} using OpenAI`, { language: faq.all_languages[translation.language_code].name })"
              />
              <!-- eslint-enable vue/no-v-html -->
              <v-btn
                :loading="openAiLoading"
                :disabled="! faq.openai_description || openAiLoading"
                color="primary"
                @click="generateTranslationUsingOpenAi"
              >
                {{ $t('Translate using OpenAI') }}
              </v-btn>
            </div>
          </open-ai-expansion-panel>

          <v-text-field
            ref="questionTitleInput"
            v-model="form.question_title"
            :loading="form.processing || openAiLoading"
            :disabled="form.processing || openAiLoading"
            :error-messages="errors.question_title"
            :label="$t('Enter question title')"
            :placeholder="question.default_translation.title"
            :hint="question.default_translation.title"
            persistent-hint
            outlined
          />

          <wysiwyg-editor
            :key="key"
            v-model="form.answer_body"
            :placeholder="$t('Enter an answer')"
            :loading="form.processing || openAiLoading"
            :disabled="form.processing || openAiLoading"
            :error-messages="errors.answer_body"
          />

          <v-btn
            small
            color="primary"
            @click="form.answer_body = question.default_translation.answer"
          >
            {{ $t('Insert answer from default language') }}
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="cancel"
          >
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            :loading="form.processing"
            :disabled="form.processing"
            type="submit"
          >
            {{ $t('Submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import WysiwygEditor from '@/components/shared/WysiwygEditor';
import OpenAiExpansionPanel from '@/components/shared/OpenAiExpansionPanel';

export default {
  components: {
    WysiwygEditor,
    OpenAiExpansionPanel
  },

  props: {
    faq: {
      type: Object,
      required: true
    },
    question: {
      type: Object,
      required: true
    },
    translation: {
      type: Object,
      required: false,
      default: null
    },
    errors: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data() {
    return {
      dialog: true,
      key: 0,
      form: this.$inertia.form('UpdateQuestionTranslationForm', {
        language_code: '',
        question_title: '',
        answer_body: ''
      }),
      openAiLoading: false
    }
  },

  computed: {
    breadcrumbs() {
      return [
        { text: 'Questions' },
        { text: this.question.default_translation.title },
        { text: this.faq.all_languages[this.translation.language_code].name }
      ];
    }
  },

  watch: {
    dialog: function(value) {
      if (value === false) {
        this.form.reset();
        const backUrl = this.route('faq.translate', this.faq.id);
        this.$inertia.visit(backUrl, { preserveState: true, preserveScroll: true });
      }
    }
  },

  mounted() {
    this.key++;
  },

  created() {
    this.form.language_code = this.translation.language_code;
    this.form.question_title = this.translation.title;
    this.form.answer_body = this.translation.answer;
  },

  methods: {
    submit() {
      const url = this.route('faq.question-translation.update', {
        faq: this.faq.id,
        question: this.question.id,
        questionTranslation: this.translation.id
      });

      this.form.patch(url, {
        preserveState: true,
        onSuccess: () => this.form.reset()
      });
    },

    cancel() {
      this.dialog = false;
    },

    goBack() {
      const url = this.route('faq.question-translation.index', [this.faq.id, this.question.id]);

      this.$inertia.visit(url, {
        preserveState: true,
        preserveScroll: true
      });
    },

    async generateTranslationUsingOpenAi() {
      const url = this.route('faq.open-ai.translate', [this.faq.id, this.question.id, this.form.language_code]);

      try {
        this.openAiLoading = true;

        const response = await axios.post(url);

        this.openAiLoading = false;

        this.form.question_title  = response.data.question;
        this.form.answer_body = response.data.answer;
      } catch (err) {
        this.openAiLoading = false;
        this.$root.$snack('error', this.$t('Something went wrong'), 1500);
      }
    }
  }
}
</script>